export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;
export const MAX_EMAIL_LENGTH = 254;

export type NewPasswordError = 'tooShort' | 'tooLong' | null;
export type EmailValidationError = 'invalid' | 'internationalLocalPart' | null;

export function getNewPasswordError(password: string): NewPasswordError {
  const length = Array.from(password.normalize('NFC')).length;
  if (length < MIN_PASSWORD_LENGTH) return 'tooShort';
  if (length > MAX_PASSWORD_LENGTH) return 'tooLong';
  return null;
}

export function newPasswordsMatch(
  password: string,
  confirmation: string,
): boolean {
  return password.normalize('NFC') === confirmation.normalize('NFC');
}

function isAscii(value: string): boolean {
  return Array.from(value).every(
    (character) => character.codePointAt(0)! <= 0x7f,
  );
}

function asciiDomain(domain: string): string {
  try {
    // URL uses the browser's standards-based IDNA implementation.
    return new URL(`http://${domain}`).hostname;
  } catch {
    return '';
  }
}

export function canonicalizeEmailForSubmit(input: string): string {
  const value = input.trim();
  const at = value.lastIndexOf('@');
  if (at <= 0 || at !== value.indexOf('@') || at === value.length - 1) {
    return value;
  }

  const localPart = value.slice(0, at);
  const domain = asciiDomain(value.slice(at + 1));
  if (!domain) return value;

  return `${localPart.toLowerCase()}@${domain.toLowerCase()}`;
}

export function isSupportedEmail(input: string): boolean {
  return getEmailValidationError(input) === null;
}

export function getEmailValidationError(input: string): EmailValidationError {
  const trimmed = input.trim();
  const at = trimmed.lastIndexOf('@');
  if (at > 0 && !isAscii(trimmed.slice(0, at))) {
    return 'internationalLocalPart';
  }

  const email = canonicalizeEmailForSubmit(input);
  if (!email || email.length > MAX_EMAIL_LENGTH || !isAscii(email)) {
    return 'invalid';
  }

  // Deliberately mirrors the practical HTML email grammar instead of trying
  // to reproduce every RFC mailbox form in client-side code.
  return /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i.test(
    email,
  )
    ? null
    : 'invalid';
}
